import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Chip,
  TextField,
  MenuItem,
  CircularProgress,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import { blueGrey, green, red, amber, blue } from '@mui/material/colors';
import * as Icons from "../constants/icons";
import { useObcSyncJob, ObcSyncJob } from '../hooks/useObcSyncJob';
import { ep } from '../api/endpoints';
import ObcAlertTargetCard from '../components/ObcAlertTargetCard';

/**
 * 🔗 Cloud Functions URL（マスタ単独同期は従来の run_obc_sync を継続利用）
 */
const SYNC_API_URL = ep('run_obc_sync');

// 年度セレクトの選択肢。OBC クラウドにデータがある 2024 年 (第31期) から
// 当年 +5 年まで (未来日付の予定伝票を同期する用途で先の年度も選べるようにする)。
const SYNC_YEAR_OPTIONS: number[] = Array.from(
  { length: new Date().getFullYear() + 5 - 2024 + 1 },
  (_, i) => 2024 + i,
);

export default function ObcDataSyncManagement() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // 指定年月
  const [syncYear, setSyncYear] = useState(new Date().getFullYear());
  const [syncMonth, setSyncMonth] = useState(new Date().getMonth() + 1);

  // 🔄 非同期ジョブ（Cloud Tasks ＋ Firestore 監視）
  const { jobs, activeJob, start } = useObcSyncJob();
  const isRunning = !!activeJob;
  // クリック直後〜起動完了（activeJob が Firestore 経由で現れる）までの空白を埋めるフラグ。
  // start() の往復（トークン取得＋API＋コールドスタート）の間ボタンを無反応にしないため。
  const [starting, setStarting] = useState(false);
  const busy = starting || isRunning;

  // 進行中ジョブが Firestore から届いたら起動中フラグを解除（以降は isRunning が引き継ぐ）
  useEffect(() => {
    if (isRunning) setStarting(false);
  }, [isRunning]);

  /**
   * 🚀 月次同期の起動（バックエンドへ委譲。fire-and-forget）
   *    起動後は他ページへ遷移してもバックエンドが完走し、進捗は Firestore で監視する。
   */
  const handleMonthlySync = async () => {
    setError(null);
    setSuccess(null);
    setStarting(true);
    try {
      await start(syncYear, syncMonth);
      setSuccess(`${syncYear}年${syncMonth}月の同期をバックエンドで開始しました。他ページへ移動しても処理は継続されます。`);
      // 成功時は activeJob が現れるまで starting を維持（上の useEffect が解除する）
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "同期の起動に失敗しました");
      setStarting(false);
    }
  };

  /**
   * 📂 マスタ同期の実行（従来どおり同期的に実行）
   */
  const handleMasterSync = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await axios.post(SYNC_API_URL, { type: 'master' });
      const counts = response.data.counts;
      if (response.data.status === "success") {
        setSuccess(`マスタ同期を完了しました (部門: ${counts?.departments}, 勘定科目: ${counts?.accounts}, 補助科目: ${counts?.sub_accounts})`);
      } else {
        setError(response.data.message || "マスタ同期に失敗しました");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || "通信エラーが発生しました");
    } finally {
      setLoading(false);
    }
  };

  const progressPct = activeJob && activeJob.total > 0
    ? (activeJob.done / activeJob.total) * 100
    : 0;

  return (
    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', minHeight: '100vh' }}>

      {/* 🏷️ Header */}
      <Box sx={{ width: '98%', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, display: 'flex', alignItems: 'center', gap: 2, color: blueGrey[500] }}>
          <Icons.Storage /> OBC データ同期管理
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748b' }}>
          奉行クラウド（勘定奉行）から最新の実績値およびマスタデータを抽出更新します。
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ width: '98%', mb: 2, borderRadius: '8px' }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ width: '98%', mb: 2, borderRadius: '8px' }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Grid container spacing={3} sx={{ width: '98%' }}>

        {/* 🛠️ Operations Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icons.Assessment sx={{ color: blue[600] }} /> 月次同期
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: '#64748b' }}>
                合計試算表に加え、仕訳明細（補助元帳ドリルダウン用）も同時に取り込みます。
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <TextField
                  select
                  fullWidth
                  label="年度"
                  value={syncYear}
                  onChange={(e) => setSyncYear(Number(e.target.value))}
                  size="small"
                  disabled={busy}
                >
                  {SYNC_YEAR_OPTIONS.map(y => <MenuItem key={y} value={y}>{y}年度</MenuItem>)}
                </TextField>
                <TextField
                  select
                  fullWidth
                  label="月"
                  value={syncMonth}
                  onChange={(e) => setSyncMonth(Number(e.target.value))}
                  size="small"
                  disabled={busy}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(m => <MenuItem key={m} value={m}>{m}月</MenuItem>)}
                </TextField>
              </Box>
              <Button
                variant="contained"
                fullWidth
                disabled={busy}
                startIcon={busy ? <CircularProgress size={20} color="inherit" /> : <Icons.Refresh />}
                onClick={handleMonthlySync}
                sx={{
                  bgcolor: blueGrey[800],
                  py: 1.5, borderRadius: '10px', fontWeight: 'bold',
                  '&:hover': { bgcolor: blueGrey[900] },
                  '&.Mui-disabled': { bgcolor: blueGrey[400], color: '#fff' }
                }}
              >
                {isRunning ? "同期を実行中..." : starting ? "同期を起動中..." : "月次データ同期を開始"}
              </Button>

              {/* 📊 進捗表示エリア（Firestore のジョブ doc を監視） */}
              {activeJob && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f1f5f9', borderRadius: '12px' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: blue[700] }}>
                      {activeJob.status === 'queued' ? '実行待機中...' : (activeJob.currentTask || '処理中...')}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      {activeJob.done} / {activeJob.total || '-'}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant={activeJob.total > 0 ? 'determinate' : 'indeterminate'}
                    value={progressPct}
                    sx={{ height: 8, borderRadius: 4, bgcolor: '#e2e8f0', '& .MuiLinearProgress-bar': { borderRadius: 4 } }}
                  />
                  <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#64748b' }}>
                    他ページへ移動しても処理は継続されます。
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Icons.Storage sx={{ color: amber[600] }} /> マスタ情報同期
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, color: '#64748b' }}>
                部門情報、勘定科目、補助科目の構成を奉行の最新状態に更新します。
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                disabled={loading}
                onClick={handleMasterSync}
                sx={{
                  py: 1.5, borderRadius: '10px', fontWeight: 'bold', borderWidth: '2px', borderStyle: 'solid'
                }}
              >
                マスタデータを一括同期
              </Button>
            </CardContent>
          </Card>

          {/* 🔔 アラート通知先（06:00 ルーティン・同期ジョブ・B/S 更新の結果/異常を Google Chat へ） */}
          <ObcAlertTargetCard />
        </Grid>

        {/* 📜 Sync History（Firestore ジョブ駆動） */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', minHeight: '500px' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Icons.History sx={{ color: green[600] }} /> 最近の同期履歴
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
                <Table stickyHeader size="medium" sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', whiteSpace: 'nowrap' }}>対象期間</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', whiteSpace: 'nowrap' }}>状態</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', whiteSpace: 'nowrap' }} align="right">進捗</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', whiteSpace: 'nowrap' }} align="right">件数</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', whiteSpace: 'nowrap' }}>開始日時</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', whiteSpace: 'nowrap' }}>完了日時</TableCell>
                      <TableCell sx={{ bgcolor: '#f8fafc', fontWeight: 'bold', whiteSpace: 'nowrap' }}>メッセージ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {jobs.map((job: ObcSyncJob) => {
                      const errMsg = job.errors.length > 0
                        ? `${job.errors.length}件のエラー (${job.errors[0].task}: ${job.errors[0].message})`
                        : '-';
                      return (
                        <TableRow key={job.id} hover>
                          <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                            {job.year && job.month ? `${job.year}年${job.month}月` : '-'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {job.status === 'completed' ? (
                              <Chip label="完了" size="small" sx={{ bgcolor: green[100], color: green[800], fontWeight: 'bold' }} />
                            ) : job.status === 'running' || job.status === 'queued' ? (
                              <Box sx={{ minWidth: '100px' }}>
                                <Chip label={job.status === 'queued' ? '待機中' : '実行中'} size="small" sx={{ bgcolor: blue[100], color: blue[800], fontWeight: 'bold' }} />
                                <LinearProgress variant="indeterminate" sx={{ mt: 1, borderRadius: '4px' }} />
                              </Box>
                            ) : (
                              <Chip label="失敗" size="small" sx={{ bgcolor: red[100], color: red[800], fontWeight: 'bold' }} />
                            )}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', color: '#64748b', fontSize: '13px' }} align="right">
                            {job.total > 0 ? `${job.done} / ${job.total}` : '-'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }} align="right">
                            {job.recordsSynced?.toLocaleString() || '-'}
                          </TableCell>
                          <TableCell sx={{ color: '#64748b', fontSize: '13px', whiteSpace: 'nowrap' }}>
                            {job.startedAt ? new Date(job.startedAt).toLocaleString('ja-JP') : '-'}
                          </TableCell>
                          <TableCell sx={{ color: '#64748b', fontSize: '13px', whiteSpace: 'nowrap' }}>
                            {job.completedAt ? new Date(job.completedAt).toLocaleString('ja-JP') : '-'}
                          </TableCell>
                          <TableCell sx={{ color: red[600], fontSize: '12px', whiteSpace: 'nowrap', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <Tooltip title={errMsg}><span>{errMsg}</span></Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {jobs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 10, color: '#94a3b8' }}>履歴がありません</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}
